import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromReporting from '../reducers/reporting.reducer';
import { selectAllScenarios } from "./scenario.selectors";
import { MarketingMode, ScenarioMarketingConfig } from "../../views/scenario/settings/models/config.models";
import { Helpers } from 'src/app/core/shared/helpers';
import { Scenario, ScenarioType } from "../../views/scenario/model/scenario.model";
import { DEFAULT_DATE_RANGES, DEFAULT_EXCLUDED_PARAMS } from 'src/app/core/app.constants';

export const selectReportingState = createFeatureSelector<fromReporting.ReportsState>(
  fromReporting.reportingFeatureKey
);

export const selectReports = createSelector(
  selectReportingState,
  (state: fromReporting.ReportsState) => state.reports
);

export const selectPersonalReports = createSelector(
  selectReportingState,
  (state: fromReporting.ReportsState) => state.personal
);

export const selectAllReports = createSelector(
  selectReportingState,
  (state: fromReporting.ReportsState) => [...state.reports, ...state.personal]
);

export const selectReport = (id: number) => createSelector(
  selectAllReports,
  (reports) => reports.find(report => report.id.toString() === id.toString())
);

export const loadingReportsState = createSelector(
  selectReportingState,
  (state) => state.loading
);

export const selectFilters = createSelector(
  selectReportingState,
  (state: fromReporting.ReportsState) => state.filters
);

export const selectDefaultFilters = (report: string) => createSelector(
  selectReportingState,
  (state: fromReporting.ReportsState) => state.initialFilters[report] ?? {}
);

export const selectBookmarkedFilters = createSelector(
  selectReportingState,
  (state: fromReporting.ReportsState) => state.bookmarkedFilters
);

export const selectFavouriteReports = createSelector(
  selectReportingState,
  (state: fromReporting.ReportsState) => {
    return new Map(state.favourites.map((favourite) => [favourite.label, {
      label: favourite.label,
      id: favourite.id
    }]));
  }
);

export const selectScenarioById = (id: number) => createSelector(
  selectAllScenarios,
  (scenarios) => scenarios.find(scenario => scenario.id === id) ?? new Scenario(id),
);

/**
 * Custom filters per dashboard
 * @param dashboard
 * @param id
 * @param scenarios
 */
export const selectFiltersByDashboardAndScenarioId = (dashboard: string, id: number, scenarios: Scenario[]) => createSelector(
    selectScenarioById(id),
    selectBookmarkedFilters,
    selectFilters,
    (state, bookmarkedFilters, filters) => {
      if (bookmarkedFilters.bookmarkedReport.includes(dashboard)) {
        return Helpers.removeKeys(DEFAULT_EXCLUDED_PARAMS, bookmarkedFilters.filters);
      } else if (['scenario_non_daily', 'scenario'].includes(dashboard)) {
        return (id && {
          ...(state.config?.marketingV2?.mode === MarketingMode.TARGET_PAYBACK && {
            'LTV DX': state.config.marketingV2.targetPayback,
            'ROAS DX': [7, state.config.marketingV2.targetPayback, 365].sort((a, b) => a - b).join(',')
          }),
        })
      } else if ('spend_recommendations' === dashboard || 'spend_recommendations_campaign' === dashboard) {
        return ((id && !!state.config?.marketingV2 && !!state.config?.inputRange?.start) && {
          'Target Dx': ScenarioMarketingConfig.isTargetROAS(state.config?.marketingV2?.mode) ?
            state.config.marketingV2.targetPayback : 365,
          'Actuals Lookback': ScenarioMarketingConfig.getPVLookbackDays(state.config?.marketingV2.spendLookback, state.config?.inputRange?.start),
        })
      } else if ('segment_checker' === dashboard) {
        return ((id && !!state.config?.marketingV2 && !!state.config?.inputRange?.start) && {
          'Actuals Lookback': ScenarioMarketingConfig.getPVLookbackDays(state.config?.marketingV2.spendLookback, state.config?.inputRange?.start),
        })
      } else if (['budget_charts', 'budget_kpis', 'portfolio_budget_comparison', 'portfolio_budget_vs_actuals'].includes(dashboard)) {
        const hasBudget = scenarios.some(scenario => scenario.type === ScenarioType.BUDGET);
        const hasDefault = scenarios.some(scenario => scenario.type === ScenarioType.DEFAULT);
        return {
          'Budget Type': (hasBudget) ? `${ ScenarioType.BUDGET }` : `${ ScenarioType.ALT_BUDGET }`,
          'Forecast Type': (hasDefault) ? `${ ScenarioType.DEFAULT }` : `${ ScenarioType.REFORECAST }`
        }
      } else if ('retention_curve_segmented' === dashboard && !!state.config?.inputRange?.end) {
        return Helpers.getRetentionCurveFilter(state.config.inputRange.end);
      } else if ('roas' === dashboard) {
        return ((id && !!state.config?.marketingV2 && !!state.config?.inputRange?.start) &&
          {
            'Current': ScenarioMarketingConfig.getPVLookbackDays(state.config?.marketingV2.spendLookback, state.config?.inputRange?.start),
            'Date Range': DEFAULT_DATE_RANGES.Daily['Date Range']
          }
        )
      } else if ('roas_trends' === dashboard || 'ua_optimisation_detail_calendar' === dashboard || 'ua_optimisation_detail_cohort' === dashboard) {
        return {
          'Target Dx': [7, state.config?.marketingV2?.targetPayback ?? 365].sort((a, b) => a - b).join(',')
        }
      } else if ('ua_optimisation' === dashboard) {
        return {
          'Aggregation Days': state.config?.aggregationDays?.toString() ?? '7',
          'Target ROAS Day': state.config?.marketingV2?.targetPayback ?? 180,
          'CPI Threshold %': state.config?.marketingV2?.cpiThreshold?.toString() ?? '25',
          'Spend Threshold %': state.config?.marketingV2?.spendThreshold?.toString() ?? '25',
          'LTV Threshold %': state.config?.marketingV2?.ltvThreshold?.toString() ?? '25',
          'Target Dx': [7, state.config.marketingV2.targetPayback ?? 365].join(", "),
        }
      }

      return {}
    }
  )
;
